import {MechanicValidator} from './MechanicValidator'
import {ArrayUtils} from '@colonist/utils'
import {ActionStatus, PieceTypes} from '@colonist/game-base-types'
import {MechanicShipState, MechanicShipPlayerState, PlayerColor, MechanicPirateState, NestedReadonly, TileEdgeType} from '@colonist/game-states'
import {getCardsForPiece} from '@colonist/game-card-types'
import {MechanicValidatorPirate} from './MechanicValidatorPirate'
import {PlayerValidator} from '../core/PlayerValidator'
import {MapValidator} from '../core/map/MapValidator'

export class MechanicValidatorShip extends MechanicValidator {
    private readonly mechanicShipState: NestedReadonly<MechanicShipState>
    private readonly pirateValidator?: MechanicValidatorPirate

    constructor(playerValidators: PlayerValidator[], mapValidator: MapValidator, mechanicShipState: NestedReadonly<MechanicShipState>, mechanicPirateState: MechanicPirateState | undefined) {
        super(playerValidators, mapValidator)

        this.mechanicShipState = mechanicShipState
        if(mechanicPirateState != undefined) this.pirateValidator = new MechanicValidatorPirate(mapValidator, mechanicPirateState)
    }

    getShipPlayerState(player: PlayerColor): NestedReadonly<MechanicShipPlayerState> {
        if(player == PlayerColor.None) {
            return {
                bankShipAmount: 0,
            }
        }
        const mechanicShipPlayerState = this.mechanicShipState[player]
        if(mechanicShipPlayerState === undefined) throw new Error(`No ship player state for player ${player}`)

        return mechanicShipPlayerState
    }

    hasResourcesAndPiecesForShip(player: PlayerColor, useResources: boolean): ActionStatus {
        const shipPlayerState = this.getShipPlayerState(player)
        if(shipPlayerState.bankShipAmount <= 0) return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.errors.notEnoughShipPieces'}}
        if(!useResources) return {canTakeAction: true}

        const resourcesForShip = getCardsForPiece(PieceTypes.Ship)
        const playerController = this.playerValidators.get(player)
        if(playerController == undefined) return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.errors.noPlayer'}}

        const hasResourcesForShip = playerController.hasResourceCardsInHand(resourcesForShip)
        if(!hasResourcesForShip) return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.errors.notEnoughResources'}}

        return {canTakeAction: true}
    }

    canPlayerPlaceShip(player: PlayerColor, useResources: boolean): ActionStatus {
        const playerController = this.playerValidators.get(player)
        if(playerController == undefined) return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.errors.noPlayer'}, validHoverLocations: []}

        const where = this.whereCanPlayerPlaceShips(player)

        const doesPlayerHaveResourcesAndPiecesForShip = this.hasResourcesAndPiecesForShip(player, useResources)
        doesPlayerHaveResourcesAndPiecesForShip.validHoverLocations = where
        if(!doesPlayerHaveResourcesAndPiecesForShip.canTakeAction) return doesPlayerHaveResourcesAndPiecesForShip

        if(where.length <= 0) return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.errors.noWhereToPlaceShip'}, validHoverLocations: where}

        return {canTakeAction: true, validHoverLocations: where}
    }

    whereCanPlayerPlaceShips(player: PlayerColor, edgeToSkip: number | undefined = undefined): number[] {
        const placesToBuildShip: number[] = []
        for(const corner of this.mapValidator.tileState.tileCorners) {
            if(corner.owner != player) continue
            if(corner.buildingType == undefined) continue
            if(this.mapValidator.isCornerKnightAndIsKnightNotTouchingEdgeType(player, corner, TileEdgeType.Ship)) continue

            for(const edge of corner.hexCorner.touchingEdges()) {
                const tileEdge = this.mapValidator.tileState.getTileEdge(edge)
                if(tileEdge == undefined) continue
                if(!this.mapValidator.tileState.isEdgeTouchingSea(tileEdge)) continue
                if(tileEdge.owner != undefined) continue
                if(tileEdge.type != undefined) continue
                if(this.pirateValidator?.isEdgeTouchingTileWithPirate(tileEdge)) continue

                const edgeIndex = this.mapValidator.tileState.getTileEdgeIndex(tileEdge.hexEdge)
                if(edgeIndex == undefined) continue
                if(placesToBuildShip.includes(edgeIndex)) continue

                placesToBuildShip.push(edgeIndex)
            }
        }

        for(const edge of this.mapValidator.tileState.tileEdges) {
            if(edge.owner != player) continue
            if(edge.type != TileEdgeType.Ship) continue
            const tileEdgeIndex = this.mapValidator.tileState.getTileEdgeIndex(edge.hexEdge)
            if(tileEdgeIndex == edgeToSkip) continue

            for(const touchingCorners of edge.hexEdge.endPoints()) {
                const tileCorner = this.mapValidator.tileState.getTileCorner(touchingCorners)
                if(tileCorner == undefined) continue
                if(tileCorner.owner != undefined) continue
                for(const touchingEdges of touchingCorners.touchingEdges()) {
                    const tileEdge = this.mapValidator.tileState.getTileEdge(touchingEdges)
                    if(tileEdge == undefined) continue
                    if(tileEdge.type != undefined) continue
                    if(!this.mapValidator.tileState.isEdgeTouchingSea(tileEdge)) continue
                    if(tileEdge.owner != undefined) continue
                    if(this.pirateValidator?.isEdgeTouchingTileWithPirate(tileEdge)) continue

                    const edgeIndex = this.mapValidator.tileState.getTileEdgeIndex(tileEdge.hexEdge)
                    if(edgeIndex == undefined) continue
                    if(placesToBuildShip.includes(edgeIndex)) continue

                    placesToBuildShip.push(edgeIndex)
                }
            }
        }

        const playerController = this.playerValidators.get(player)
        if(playerController == undefined) return placesToBuildShip

        const shipPlayerState = this.getShipPlayerState(playerController.color)
        for(const restrictedPlacement of shipPlayerState.restrictedShipPlacements ?? []) {
            ArrayUtils.removeElementFromArray(placesToBuildShip, restrictedPlacement)
        }
        return placesToBuildShip
    }

    whereCanPlayerPlaceBeginningShips(player: PlayerColor): number[] {
        const placeToBuildShip: number[] = []
        for(const corner of this.mapValidator.tileState.tileCorners) {
            if(corner.owner != player) continue
            if(corner.buildingType == undefined) continue
            if(!this.mapValidator.areAdjacentEdgesEmpty(corner)) continue

            for(const edge of corner.hexCorner.touchingEdges()) {
                const tileEdge = this.mapValidator.tileState.getTileEdge(edge)
                if(tileEdge == undefined) continue
                if(!this.mapValidator.tileState.isEdgeTouchingSea(tileEdge)) continue
                if(this.pirateValidator?.isEdgeTouchingTileWithPirate(tileEdge)) continue

                const tileIndex = this.mapValidator.tileState.getTileEdgeIndex(tileEdge.hexEdge)
                if(tileIndex == undefined) continue

                placeToBuildShip.push(tileIndex)
            }
        }
        return placeToBuildShip
    }
}
